import React, { useEffect, useState } from 'react';
import CustomTable from '../../../../../components/table/CustomTable';
import CustomTh from '../../../../../components/table/CustomTh';
import CustomTd from '../../../../../components/table/CustomTd';
import TableFunction from '../../../../../components/table/TableFunction';
import NotInformation from '../../../../../components/NotInformation';
import { OutlinedCalendarIcon } from '../../../../../components/buttons/IconButtons';
import { EMPTY_VALUES } from '../../../../../Apis/functions';
import { LEFT_FIRST_COLUMN_WITH_CHECKBOX } from '../../../../../components/table/Model/TableModel';
import dayId from '../../../../../hooks/dayId';

/**
 * Author: Arnulfo Ferro Zapata
 * Job Title: Fullstack
 * Email: azapata@ibl.mx
 *
 * Client-side schedules table for both create and update. Renders the buffered
 * work shift schedules (zone + day + from + to) with checkbox selection and
 * local deletion. The buffer is the single source of truth: the checkbox selects
 * rows and the header delete button (via `triggerDelete`) removes them from the
 * buffer with no server round-trip and no confirmation modal. Pagination is
 * resolved client-side over the buffer. The buffer is submitted on create /
 * update; nothing is committed until then.
 *
 * @param {Object} props
 * @param {Array<Object>} props.schedules - Buffered schedules (keyed by id).
 * @param {Function} props.setSchedules - Buffer setter.
 * @param {Array} props.selectForDelete - Selected ids.
 * @param {Function} props.setSelectForDelete - Selection setter.
 * @param {boolean} props.triggerDelete - When true, drops the selected rows from the buffer.
 * @param {Function} props.setTriggerDelete - Trigger setter (reset after deletion).
 */
const BufferSchedulesTable = ({ schedules, setSchedules, selectForDelete, setSelectForDelete,
                                  triggerDelete, setTriggerDelete }) => {
    const [isAllChecked, setIsAllChecked] = useState(false);
    const [results, setResults] = useState([]);
    const tableId = 'tbl_bst_buffer_schedules';
    const leftPosition = LEFT_FIRST_COLUMN_WITH_CHECKBOX;

    const data = { content: results, totalElements: (schedules || []).length };

    const getThead = () => [
        <CustomTh tableId={tableId} fixedXY={true} left={leftPosition} classes='first_head'>
            {t('zones').toUpperCase()}
        </CustomTh>,
        <CustomTh tableId={tableId}>{t('day').toUpperCase()}</CustomTh>,
        <CustomTh tableId={tableId}>{t('from').toUpperCase()}</CustomTh>,
        <CustomTh tableId={tableId}>{t('to').toUpperCase()}</CustomTh>,
    ];

    const getTbody = () => {
        if (!data.content.length) return { body: [], data: [] };
        const body = data.content.map((item) => {
            return (
                <>
                    <CustomTd tableId={tableId} tooltip={item?.zoneName} fixed={false} left={leftPosition} classes={`first-column`}>
                        {item?.zoneName || EMPTY_VALUES.DEFAULT}
                    </CustomTd>
                    <CustomTd tableId={tableId}> <OutlinedCalendarIcon className={'mr-2'} width={16} height={16}/> {dayId(item?.dayId)}</CustomTd>
                    <CustomTd  tableId={tableId}>{item?.from || EMPTY_VALUES.DEFAULT}</CustomTd>
                    <CustomTd  tableId={tableId}>{item?.to || EMPTY_VALUES.DEFAULT}</CustomTd>
                </>
            );
        });
        return { body, data: data.content };
    };

    const {
        t,
        thead,
        tbody,
        handleCheckboxChange,
        handleDeleteAll,
        pagination,
        page,
        rowsPerPage,
    } = TableFunction({
        fixedPagination: false,
        hasContainer: false,
        selectedList: selectForDelete,
        setSelectedList: setSelectForDelete,
        setIsAllChecked,
        data,
        getThead,
        getTbody,
        labelRowsPerPage: 'access_per_page',
        dependencies: [selectForDelete],
    });

    /**
     * Resolves the current page slice from the buffer client-side. Re-runs on
     * pagination changes and whenever the buffer itself changes.
     */
    useEffect(() => {
        setResults((schedules || []).slice(page * rowsPerPage, (page + 1) * rowsPerPage));
    }, [page, rowsPerPage, schedules]);

    /**
     * Drops the selected rows from the buffer when the header delete button fires
     * the trigger. No endpoint, no confirmation modal: the removal is committed
     * only when the user creates / updates.
     */
    useEffect(() => {
        if (!triggerDelete) return;
        setSchedules((prev) => (prev || []).filter((s) => !selectForDelete.includes(s.id)));
        setSelectForDelete([]);
        setIsAllChecked(false);
        setTriggerDelete(false);
    }, [triggerDelete]);

    return (
        <>
            {(schedules || []).length > 0
                ? <>
                    <CustomTable
                        tableId={tableId}
                        checkbox={true}
                        checkboxTitle={'de_/_select_all'}
                        isAllChecked={isAllChecked}
                        onChangeCheckbox={handleDeleteAll}
                        thead={thead}
                        tbody={tbody}
                        columnActions={false}
                        handleCheckboxChange={handleCheckboxChange}
                        selectedList={selectForDelete}
                        withoutHeight={false}
                    />
                    {pagination()}
                </>
                : <NotInformation text={t('no_custom_shifts_added_yet')} card={false} />
            }
        </>
    );
};

export default BufferSchedulesTable;
